import React, { useState, useEffect } from 'react';
import { Language, SiteBranding } from '../types';
import { translations } from '../i18n/translations';
import { getSiteBranding } from '../services/siteSettingsStorage';

interface HeroProps {
  currentLang: Language;
  onNavigate: (page: string) => void;
  onQuickVerify: (id: string) => void;
}

export const Hero: React.FC<HeroProps> = ({ currentLang, onNavigate }) => {
  const [branding, setBranding] = useState<SiteBranding>(getSiteBranding());
  const t = translations[currentLang];

  useEffect(() => {
    const handleBrandingChange = (e: Event) => {
      const customEvent = e as CustomEvent<SiteBranding>;
      if (customEvent.detail) setBranding(customEvent.detail);
      else setBranding(getSiteBranding());
    };
    window.addEventListener('chc_site_branding_updated', handleBrandingChange);
    return () => window.removeEventListener('chc_site_branding_updated', handleBrandingChange);
  }, []);

  return (
    <section className="bg-slate-50 dark:bg-slate-950 pt-16 pb-12 transition-colors">
      <div className="w-[90%] max-w-none mx-auto px-6 grid grid-cols-1 gap-12">
        <div className="w-[90%] lg:w-[70%] max-w-none text-left rtl:text-right">
          <span className="inline-block px-2.5 py-1 mb-6 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[10px] uppercase font-bold tracking-widest rounded-sm">
            {currentLang === 'ar' ? 'مكتب خبرة' : currentLang === 'en' ? 'CONSULTING FIRM' : "CABINET D'EXPERTISE"}
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-5xl font-semibold text-slate-900 dark:text-white leading-[1.1] font-sans tracking-tight whitespace-pre-line mb-6">
            {branding.heroTitle || (currentLang === 'ar' ? 'التميز المؤسسي والعملياتي،\nفي خدمة أدائكم الاستراتيجي.' : currentLang === 'en' ? "Strategic IT Excellence & Global Standards." : "L'Excellence Opérationnelle,\nNotre Engagement.")}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-base w-[90%] lg:w-[60%] max-w-none leading-relaxed mb-8">
            {branding.heroSubtitle || (currentLang === 'ar' ? 'مرافقة استراتيجية، تدقيق دقيق لتكنولوجيا المعلومات، ودمج لمعايير ISO لدفع مؤسستكم نحو مستويات التميز الدولية.' : currentLang === 'en' ? "Empowering high-end consulting sectors with unparalleled IT audit, ISO certification support, and robust strategic expertise." : "Accompagnement stratégique, audit IT de précision et intégration des normes ISO pour propulser votre entreprise vers les standards internationaux.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => onNavigate('expertises')}
              className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-6 py-3.5 text-xs font-semibold hover:bg-slate-800 dark:hover:bg-slate-200 transition-colors rounded-sm w-full sm:w-auto text-center"
            >
              {currentLang === 'en' ? 'Explore Services' : t.hero.ctaServices}
            </button>
            <button
              onClick={() => onNavigate('contact')}
              className="bg-transparent border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white px-6 py-3.5 text-xs font-semibold hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors rounded-sm w-full sm:w-auto text-center"
            >
              {currentLang === 'en' ? 'Contact Us' : t.hero.ctaContact}
            </button>
          </div>
        </div>
        
        <div className="w-full h-64 md:h-[400px] overflow-hidden rounded-sm">
          <img 
            src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&q=80&w=2070" 
            alt="Consulting Meeting"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </section>
  );
};
